import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable, switchMap, throwError, from } from 'rxjs';
import { AuthService } from './services/auth/auth-service';
import { catchError } from 'rxjs/operators';
import { Token } from './models/token';
import { environment } from '../environments/environment';
import { LOCAL_STORAGE_KEYS } from './constants/storage-keys';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptorService implements HttpInterceptor {
  private lastIntrospectionTime: number = 0;
  private lastIntrospectionResult: boolean = false;
  private readonly CACHE_DURATION_MS: number = 60000; // 1 minute cache

  constructor(private readonly injector: Injector) {}

  private static shouldBypass(request: HttpRequest<any>): boolean {
    // Bypass CAS endpoints when Keycloak is not active
    if (
      request.url &&
      !environment.KEYCLOAK_ACTIVE &&
      environment.CAS_URL &&
      request.url.startsWith(environment.CAS_URL)
    ) {
      return true;
    }
    // Always bypass token introspection endpoint itself
    if (request.url?.includes('/protocol/openid-connect/token/introspect')) {
      return true;
    }
    return false;
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (AuthInterceptorService.shouldBypass(request)) {
      return next.handle(request);
    }

    let jwt = localStorage.getItem(Token.tokenNameInStorage);
    const language =
      localStorage.getItem(LOCAL_STORAGE_KEYS.LANGUAGE) ??
      environment.DEFAULT_LANGUAGE ??
      'en';

    // In CAS mode, if we don't yet have a JWT (e.g., just returned with ?code=... and
    // the initializer is still persisting tokens), wait briefly for the token to appear
    // before proceeding with the request. This prevents initial 403s.
    if (!environment.KEYCLOAK_ACTIVE && !jwt) {
      const waitForToken = new Promise<string | null>((resolve) => {
        let tries = 0;
        const maxTries = 60; // ~3s @ 50ms
        const interval = setInterval(() => {
          const t = localStorage.getItem(Token.tokenNameInStorage);
          if (t || tries++ >= maxTries) {
            clearInterval(interval);
            resolve(t);
          }
        }, 50);
      });

      return from(waitForToken).pipe(
        switchMap((maybeToken) => {
          jwt = maybeToken ?? null;
          const requestWithMaybeAuth = jwt
            ? request.clone({
                setHeaders: {
                  'Content-Type': 'application/json',
                  'Content-Language': language,
                  Authorization: `Bearer ${jwt}`
                }
              })
            : request;

          if (!environment.ENABLE_TOKEN_INTROSPECT || !jwt) {
            return next.handle(requestWithMaybeAuth);
          }

          const authService = this.injector.get(AuthService);
          const currentTime = Date.now();
          if (
            currentTime - this.lastIntrospectionTime <
            this.CACHE_DURATION_MS
          ) {
            if (!this.lastIntrospectionResult) {
              authService.logout(window.location.origin).subscribe();
            }
            return next.handle(requestWithMaybeAuth);
          }

          return authService.introspectToken(jwt).pipe(
            switchMap((isActive) => {
              this.lastIntrospectionTime = Date.now();
              this.lastIntrospectionResult = isActive;

              if (!isActive) {
                authService.logout(window.location.origin).subscribe();
              }

              return next.handle(requestWithMaybeAuth);
            }),
            catchError((err) => {
              console.error('Error during token introspection:', err);
              return throwError(() => err);
            })
          );
        })
      );
    }

    const requestWithAuth = jwt
      ? request.clone({
          setHeaders: {
            'Content-Type': 'application/json',
            'Content-Language': language,
            Authorization: `Bearer ${jwt}`
          }
        })
      : request;

    if (!environment.ENABLE_TOKEN_INTROSPECT) {
      return next.handle(requestWithAuth);
    }

    if (!jwt) {
      return next.handle(requestWithAuth);
    }

    const authService = this.injector.get(AuthService);

    const currentTime = Date.now();
    if (currentTime - this.lastIntrospectionTime < this.CACHE_DURATION_MS) {
      if (!this.lastIntrospectionResult) {
        authService.logout(window.location.origin).subscribe();
      }
      return next.handle(requestWithAuth);
    }

    return authService.introspectToken(jwt).pipe(
      switchMap((isActive) => {
        this.lastIntrospectionTime = Date.now();
        this.lastIntrospectionResult = isActive;

        if (!isActive) {
          authService.logout(window.location.origin).subscribe();
        }

        return next.handle(requestWithAuth);
      }),
      catchError((err) => {
        console.error('Error during token introspection:', err);
        return throwError(() => err);
      })
    );
  }
}
